import { ref, readonly } from 'vue'
import { registerSW } from 'virtual:pwa-register'
import { captureError } from '@/utils/sentry'
import { createLogger } from '@/utils/logger'

const logger = createLogger('ServiceWorker')

// Service worker update check interval in milliseconds
// Note: 60 seconds is more aggressive than typical (60 minutes is common)
// but provides faster update delivery for active users.
const SW_UPDATE_INTERVAL_MS = 60 * 1000

// Module-level state (shared across components)
const needRefresh = ref(false)
const offlineReady = ref(false)
const isRegistered = ref(false)

let updateSW: ((reloadPage?: boolean) => Promise<void>) | undefined

export function useServiceWorker() {
  const updateServiceWorker = async (reloadPage = true): Promise<void> => {
    if (updateSW) {
      await updateSW(reloadPage)
    }
  }

  return {
    needRefresh: readonly(needRefresh),
    offlineReady: readonly(offlineReady),
    isRegistered: readonly(isRegistered),
    updateServiceWorker,
  }
}

export function initServiceWorker(): void {
  // Skip in development mode (bun run dev)
  if (import.meta.env.MODE === 'development') {
    logger.info('Disabled in development mode')
    return
  }

  // Skip if running on localhost (prevents SW when testing prod build locally)
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  if (isLocalhost) {
    logger.info('Service worker disabled on localhost')
    return
  }

  // Service worker will register for staging and production deployments
  logger.info('Initializing service worker for production deployment')

  updateSW = registerSW({
    onNeedRefresh() {
      logger.info('New app version available')
      needRefresh.value = true
    },
    onOfflineReady() {
      logger.info('App ready for offline use')
      offlineReady.value = true
    },
    onRegistered(registration: ServiceWorkerRegistration | undefined) {
      logger.info('Service worker registered')
      isRegistered.value = true
      // Check for updates every 60 seconds
      if (registration) {
        setInterval(() => {
          registration.update()
        }, SW_UPDATE_INTERVAL_MS)
      }
    },
    onRegisterError(error: Error) {
      logger.error('Registration failed:', error)
      captureError(error, { source: 'service-worker', event: 'registration-failed' })
    },
  })
}
